import type { MeContext, OrgNode, PortfolioNode } from '@vizit/api';
import type { LandingDecision } from '../types';
import { getAccessiblePortfolios, getAccessibleWorkspaces } from './get-accessible';
import { isSuperAdmin } from './is-super-admin';
import { hasElevatedGrant } from './manage-users';

function resolveSingleWorkspace(portfolio: PortfolioNode): LandingDecision {
  const workspace =
    portfolio.workspaces.find((candidate) => candidate.id === portfolio.defaultWorkspaceId) ?? portfolio.workspaces[0];
  if (!workspace) {
    return { type: 'noAccess' };
  }
  return {
    type: 'workspace',
    workspaceId: workspace.id,
    workspaceSlug: workspace.slug,
  };
}

// Decision type is driven by org TOTALS; payload ids still come from the accessible subtree.
function resolveByOrgTotals(org: OrgNode): LandingDecision {
  if (org.portfolioCount > 1) {
    return { type: 'portfolioPicker' };
  }

  if (org.portfolioCount === 1 && org.workspaceCount > 1) {
    const portfolioId = org.portfolios[0]?.id ?? org.defaultPortfolioId;
    if (!portfolioId) {
      return { type: 'noAccess' };
    }
    return { type: 'workspacePicker', portfolioId };
  }

  if (org.portfolioCount === 1 && org.workspaceCount === 1) {
    const portfolio = org.portfolios.find((node) => node.id === org.defaultPortfolioId) ?? org.portfolios[0];
    if (!portfolio) {
      return { type: 'noAccess' };
    }

    return resolveSingleWorkspace(portfolio);
  }

  return { type: 'noAccess' };
}

// Pure: skip every level that resolves to exactly one child. Never fetches or navigates.
export function resolveLanding(context: MeContext): LandingDecision {
  const superAdmin = isSuperAdmin(context);

  if (!superAdmin && context.grants.length === 0) {
    return { type: 'noAccess' };
  }

  if (superAdmin) {
    return resolveByOrgTotals(context.organization);
  }

  const portfolios = getAccessiblePortfolios(context);
  const workspaces = getAccessibleWorkspaces(context);
  const portfolioIdsWithWorkspaces = new Set(workspaces.map((workspace) => workspace.portfolioId));

  if (!hasElevatedGrant(context) && portfolioIdsWithWorkspaces.size > 1) {
    return { type: 'flatWorkspacePicker' };
  }

  if (portfolios.length > 1) {
    return { type: 'portfolioPicker' };
  }

  if (portfolios.length === 1) {
    const portfolio = portfolios[0];
    if (portfolio.workspaces.length > 1) {
      return { type: 'workspacePicker', portfolioId: portfolio.id };
    }
    if (portfolio.workspaces.length === 1) {
      return resolveSingleWorkspace(portfolio);
    }
    return { type: 'noAccess' };
  }

  return { type: 'noAccess' };
}
